import { APP_SETTINGS } from "../../shared/app_settings";
import { byId, cloneTemplate, fillFields, renderList } from "../dom";
import { onViewShown } from "../navigation";
import { flash } from "../tweens";
import { calculate, formatResult } from "./calculator_parser";

// keypad calculator with a live preview and a history tape

interface TapeLine {
  expression: string;
  result: string;
}

const HISTORY_KEY = "calculator-tape";
let tape: TapeLine[] = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]");

const display = () => byId<HTMLInputElement>("calc-display");

function preview(): void {
  try {
    byId("calc-result").textContent = `= ${formatResult(calculate(display().value))}`;
    byId("calc-result").classList.remove("broken");
  } catch {
    byId("calc-result").classList.add("broken");
  }
}

function renderTape(): void {
  renderList(byId("calc-history"), tape, (line) => {
    const row = fillFields(cloneTemplate("template-history"), { expression: line.expression, result: `= ${line.result}` });
    // tapping an old line brings its answer back
    row.addEventListener("click", () => setDisplay(line.result.replace(/,/g, "")));
    return row;
  });
}

function setDisplay(text: string): void {
  display().value = text;
  preview();
}

function evaluate(): void {
  try {
    const result = formatResult(calculate(display().value));
    if (display().value.trim()) {
      tape = [{ expression: display().value, result }, ...tape].slice(0, APP_SETTINGS.calculatorHistorySize);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(tape));
      renderTape();
    }
    setDisplay(result.replace(/,/g, ""));
    flash(byId("calc-result"));
  } catch (error) {
    byId("calc-result").textContent = String(error).replace(/^Error: /, "");
    flash(display());
  }
}

function pressKey(key: string): void {
  if (key === "=") return evaluate();
  if (key === "C") return setDisplay("");
  if (key === "back") return setDisplay(display().value.slice(0, -1));
  setDisplay(display().value + key);
}

export function setupCalculatorView(): void {
  byId("calc-keys").querySelectorAll<HTMLButtonElement>("[data-key]").forEach((button) => {
    button.addEventListener("click", () => {
      pressKey(button.dataset.key!);
      flash(button);
    });
  });

  display().addEventListener("input", preview);
  display().addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === "=") {
      event.preventDefault();
      evaluate();
    } else if (event.key === "Escape") setDisplay("");
  });

  byId("calc-clear-history").addEventListener("click", () => {
    tape = [];
    localStorage.removeItem(HISTORY_KEY);
    renderTape();
  });

  onViewShown("calculator", () => display().focus());
  renderTape();
}
